import {
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { DeleteFilled, SparkleRegular, ArrowSplitFilled } from '@fluentui/react-icons';
import { useEffect, useRef } from 'react';
import { telemetryService } from '@/services/telemetryService';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

const useStyles = makeStyles({
  menuContainer: {
    position: 'fixed',
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    minWidth: '200px',
  },
});

interface NodeContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  nodeId: string | null;
  nodeType: 'trigger' | 'action' | 'tool' | null;
  onClose: () => void;
  onDelete: () => void;
  onEditWithAssistant?: () => void;
  onAddParallelBranch?: (nodeId: string) => void;
}

export const NodeContextMenu = ({
  isOpen,
  position,
  nodeId,
  nodeType,
  onClose,
  onDelete,
  onEditWithAssistant,
  onAddParallelBranch,
}: NodeContextMenuProps) => {
  const styles = useStyles();
  const isScopedEditEnabled = useFeatureFlag('scopedEdit');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !position || !nodeId) return null;

  const nodeTypeName = nodeType === 'trigger' ? 'Trigger' : nodeType === 'tool' ? 'Tool' : 'Action';
  const canAddParallelBranch = onAddParallelBranch && (nodeType === 'trigger' || nodeType === 'action');

  return (
    <div
      ref={menuRef}
      className={styles.menuContainer}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <MenuList>
        {onEditWithAssistant && isScopedEditEnabled && (
          <>
            <MenuItem
              icon={<SparkleRegular />}
              onClick={() => {
                onEditWithAssistant();
                onClose();
                telemetryService.trackEvent('canvas.node.context_menu.action', { action: 'edit_with_assistant', nodeType: nodeType ?? 'unknown' });
              }}
            >
              Edit with Copilot
            </MenuItem>
            <MenuDivider />
          </>
        )}
        {canAddParallelBranch && (
          <MenuItem
            icon={<ArrowSplitFilled />}
            onClick={() => {
              onAddParallelBranch(nodeId);
              onClose();
              telemetryService.trackEvent('canvas.node.context_menu.action', { action: 'add_parallel_branch', nodeType: nodeType ?? 'unknown' });
            }}
          >
            Add parallel branch
          </MenuItem>
        )}
        <MenuItem
          icon={<DeleteFilled />}
          onClick={() => {
            onDelete();
            onClose();
            telemetryService.trackEvent('canvas.node.context_menu.action', { action: 'delete', nodeType: nodeType ?? 'unknown' });
          }}
        >
          Delete {nodeTypeName}
        </MenuItem>
      </MenuList>
    </div>
  );
};
